/**
 * Persistent account stocks. One row per account, reused across refills — the
 * daily-stock model (stock_date, stock_index, the daily copy, and the old
 * free-text status set) is gone.
 */
export type StockStatus = 'NotReady' | 'Ready' | 'Transferring' | 'Done' | 'Failed' | 'Archived';

export const STOCK_STATUSES: StockStatus[] = [
  'NotReady',
  'Ready',
  'Transferring',
  'Done',
  'Failed',
  'Archived',
];

export type PaymentStatus = 'Payed' | 'NotPayed';

export interface Stock {
  id: string;
  login_info_email: string;
  login_info_id?: string;

  /** Stable owner. `supplier` is a legacy display name and is NOT an owner key. */
  supplier_id: string;
  supplier_name: string;
  supplier?: string;

  real_initial_coins: number;
  remained_coins: number;
  /** Stop threshold: draining continues while the balance is above this. */
  coins_to_be_remain: number;
  status: StockStatus;

  ready_at?: string | null;
  disarm_requested_at?: string | null;
  transfer_started_at?: string | null;
  last_run_id?: string;
  fail_detail?: string;
  /**
   * Stable diagnosis code for fail_detail. Empty when fail_detail is unset or is
   * an admin-only detail with no client-facing code — including the buyer-side
   * failures, which are deliberately never blamed on the supplier's account.
   * See STOCK_PROBLEM_LABELS in stock-problems.ts for the codes rendered here.
   */
  problem_code?: string;

  description?: string;
  createUserId?: string;
  updateUserId?: string;
  createUsername?: string;
  updateUsername?: string;
  created_at: string;
  updated_at: string;

  /** Progress of the run in flight. NOT lifetime sales — accounts get refilled. */
  current_run_coins: number;
  estimated_payable: number;
  /** Where a run really stops, once the platform minimum credit is applied. */
  effective_floor: number;
  floor_raised_by_minimum: boolean;
}

export interface StockCounts {
  total: number;
  not_ready: number;
  ready: number;
  transferring: number;
  done: number;
  failed: number;
  /** Not part of total: archived accounts are left out of the unfiltered list. */
  archived: number;
}

export interface GetStocksRequest {
  pageIndex?: number;
  pageSize?: number;
  /** Admin-only: narrow to one supplier by user id. */
  supplierId?: string;
  status?: StockStatus | '';
  search?: string;
}

export interface GetStocksResponse {
  stocks: Stock[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
  counts?: StockCounts;
}

export interface CreateStockRequest {
  login_info_email: string;
  coins_to_be_remain?: number;
  description?: string;
  /** Admin-only: create on behalf of a supplier. */
  supplier_id?: string;
}

/** Supplier-editable detail only. Fee, status and ownership are not here. */
export interface UpdateStockRequest {
  description?: string;
}

export interface MarkReadyRequest {
  coins_to_be_remain: number;
}

export interface BulkReadinessRequest {
  ids: string[];
  coins_to_be_remain: number;
}

export interface BulkReadinessResponse {
  updated: number;
  skipped?: { id: string; reason: string }[];
}

export interface DeleteStocksRequest {
  ids: string[];
}

export interface DeleteStocksResponse {
  deleted: number;
}

// ── Transfer runs ───────────────────────────────────────────────────────────

export interface TransferRun {
  id: string;
  stock_id: string;
  supplier_id: string;
  supplier_name?: string;
  buy_contract_id: string;
  login_info_email?: string;
  status: string;
  failed_reason?: string;
  start_coins: number;
  last_coins: number;
  requested_floor: number;
  coins_transferred: number;
  cards_bought: number;
  /** The rate as it stood when this run was claimed. */
  fee_snapshot: number;
  payable_amount: number;
  payment_status: PaymentStatus;
  settlement_id?: string;
  started_at?: string | null;
  finished_at?: string | null;
  confirmed_at?: string | null;
  created_at?: string | null;
}

export interface GetTransferRunsRequest {
  pageIndex?: number;
  pageSize?: number;
  supplierId?: string;
  stockId?: string;
  status?: string;
  paymentStatus?: string;
  /** YYYY-MM-DD in the reporting timezone the API documents. */
  fromDate?: string;
  toDate?: string;
  /** Admin-only: include runs that transferred no coins. Suppliers never see them. */
  includeZeroCoinRuns?: boolean;
  /**
   * Hide runs still in flight (Pending/Active): they are not payable until
   * finished. Left off for per-account history, where live runs are wanted.
   */
  payableOnly?: boolean;
}

export interface GetTransferRunsResponse {
  runs: TransferRun[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}

export interface DailyReportRow {
  date: string;
  run_count: number;
  coins_transferred: number;
  payable_amount: number;
  paid_amount: number;
  unpaid_amount: number;
}

export interface DailyReportResponse {
  /** Echoed by the API so no client hardcodes the reporting zone. */
  timezone: string;
  rows: DailyReportRow[];
  total: number;
  pageIndex: number;
  pageSize: number;
  totalPages: number;
}
